'use client';

import { useCallback } from 'react';
import {
	getTokenAddressForDepositAndWithdraw,
	UIMarket,
} from '@velocity-exchange/common';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { notify } from 'src/utils/notifications';
import { useGetTxParams } from '../transactions/useGetTxParams';

interface WithdrawInsuranceFundAssetsOptions {
	marketIndex: number;
}

export const useWithdrawInsuranceFundAssets = () => {
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const wallet = useDriftStore((s) => s.wallet.current.adapter);
	const getTxParams = useGetTxParams();

	const withdrawInsuranceFundUnstakedAssets = useCallback(
		async (opts: WithdrawInsuranceFundAssetsOptions): Promise<boolean> => {
			if (!driftClient || !wallet?.publicKey) return false;

			const spotMarketConfig = UIMarket.spotMarkets.find(
				(m) => m.marketIndex === opts.marketIndex
			);
			const toastId = `withdraw-unstaked-assets-${opts.marketIndex}`;

			notify({
				id: toastId,
				type: 'awaiting_wallet_confirmation',
				showUntilCancelled: true,
			});

			try {
				const authority = wallet.publicKey;

				const spotMarketAccount = driftClient.getSpotMarketAccount(
					spotMarketConfig?.marketIndex ?? 0
				);

				if (!spotMarketAccount) {
					throw new Error('Spot market account not found');
				}

				const tokenAccount = await getTokenAddressForDepositAndWithdraw(
					spotMarketAccount,
					authority
				);

				const _txid = await driftClient?.removeInsuranceFundStake(
					opts.marketIndex,
					tokenAccount,
					getTxParams()
				);

				notify({
					id: toastId,
					type: 'success',
					message: 'Withdrew unstaked.',
					showUntilCancelled: false,
				});

				return true;
			} catch (err: any) {
				console.log(err);
				notify({
					id: toastId,
					type: 'error',
					message: 'Failed to withdraw.',
					showUntilCancelled: false,
				});
				return false;
			}
		},
		[driftClient, wallet, getTxParams]
	);

	return { withdrawInsuranceFundUnstakedAssets };
};
